// Nota de turno de enfermería SOAPIE (código propio `enf-soapie`). NO es un
// formulario del MSP — no está en el Anexo 1 ni en docs/msp/: es la nota
// narrativa de turno que Sue eligió sumar al 020/022/120 ("MSP + nota
// SOAPIE"). Por eso el PDF sale en modo 'pares' (encabezado genérico, sin
// encabezado ni leyenda SNS-MSP/HCU-form). Un Composition por paciente y
// turno con 6 secciones (Subjetivo, Objetivo, Análisis, Planificación,
// Intervención, Evaluación) — ver construirNotaSoapie en enfermeria.ts.
import type { Composition } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { construirNotaSoapie, crearUnaVez, horaDeRegistro, diaEnfermeriaDe, SECCIONES_SOAPIE, turnoDeHora, type ClaveSoapie, type Turno } from './enfermeria';
import { Responsable, SelectorDiaEnfermeria, SelectorTurno } from './SelectorTurno';
import { useResponsable } from './useResponsable';

export function FormularioSoapie({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const responsable = useResponsable();
  const [turno, setTurno] = useState<Turno>(() => turnoDeHora(new Date().getHours()));
  const [dia, setDia] = useState(() => diaEnfermeriaDe(new Date()));
  const [textos, setTextos] = useState<Partial<Record<ClaveSoapie, string>>>({});
  const { guardando, error, guardar } = useGuardado();

  const hayTexto = SECCIONES_SOAPIE.some(({ clave }) => textos[clave]?.trim());

  const handleSubmit = (): Promise<void> => guardar(async () => {
    if (!hayTexto) throw new Error(t('formularios.fSoapie.sinTexto'));
    // Composition.author es 1..*: sin profesional de sesión Medplum la rechaza.
    if (!responsable) throw new Error(t('enfermeria.sinResponsableError'));
    const ahora = new Date();
    const composition = construirNotaSoapie({ subject, encounter, autor: responsable, turno, dia, textos, ahora });
    const pares: Par[] = [
      { pregunta: t('enfermeria.turno'), respuesta: t(`enfermeria.turnos.${turno}`) },
      { pregunta: t('enfermeria.diaEnfermeria'), respuesta: dia },
      { pregunta: t('formularios.fSoapie.horaRegistro'), respuesta: horaDeRegistro(ahora) },
      { pregunta: t('enfermeria.responsable'), respuesta: responsable?.display ?? t('enfermeria.sinResponsable') },
      ...SECCIONES_SOAPIE.map(({ clave }) => ({ pregunta: t(`formularios.fSoapie.${clave}`), respuesta: textos[clave]?.trim() || t('formularios.fSoapie.sinRegistro') })),
    ];
    // Orden seguro (Ola 3): el Composition NO se crea acá. Se crea recién cuando el PDF ya se generó y firmó en el servidor
    // (`PanelFirmaImpresion` llama a esta función después de `firmarDocumentoEnServidor`), así nunca queda una nota sin PDF
    // por una firma fallida. Va memoizado: si después de crearla falla el guardado del PDF y se reintenta la firma, se
    // reutiliza la misma nota en vez de duplicarla. El Composition queda referenciado desde el DocumentReference del PDF
    // firmado (`context.related`) — así la pestaña "Nota de turno" ofrece el PDF junto al detalle de la nota.
    onGuardado(pares, undefined, crearUnaVez(async () => {
      const creada = await medplum.createResource<Composition>(composition);
      // Recién ahora la nota existe: el formulario se limpia. Si la firma falla, el texto sigue en pantalla.
      setTextos({});
      return { reference: `Composition/${creada.id}` };
    }));
  });

  return (
    <div className="flex flex-col gap-4">
      <Alert>
        <AlertDescription>{t('formularios.fSoapie.aviso')}</AlertDescription>
      </Alert>
      <div className="flex flex-wrap items-end gap-4">
        <SelectorTurno turno={turno} onChange={setTurno} />
        <SelectorDiaEnfermeria id="fsoapie-dia" dia={dia} onChange={setDia} />
      </div>
      <Responsable display={responsable?.display} />

      {SECCIONES_SOAPIE.map(({ clave }) => (
        <div key={clave} className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">{t(`formularios.fSoapie.ayuda${clave}`)}</p>
          <AreaDictado id={`fsoapie-${clave}`} etiqueta={t(`formularios.fSoapie.${clave}`)} valor={textos[clave] ?? ''} onChange={(v) => setTextos((prev) => ({ ...prev, [clave]: v }))} filas={3} />
        </div>
      ))}

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando || !hayTexto}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
